import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  GitCompare, Upload, Link, CheckCircle, XCircle,
  AlertTriangle, Info, Download, ChevronDown, ChevronRight,
  BarChart2, FileText, Layers, ArrowRight, Server
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const METRICS = [
  { key: 'impressions', label: 'Impressions',  format: 'integer' },
  { key: 'revenue',     label: 'Revenue',      format: 'currency' },
];

const SEVERITY_COLORS = {
  ok:         { bg: 'color-mix(in srgb, var(--cyan) 6%, transparent)',  border: 'color-mix(in srgb, var(--cyan) 20%, transparent)',  text: 'var(--cyan)',  badge: 'badge-cyan'   },
  warning:    { bg: 'color-mix(in srgb, var(--amber) 8%, transparent)', border: 'color-mix(in srgb, var(--amber) 20%, transparent)',  text: 'var(--amber)', badge: 'badge-amber'  },
  critical:   { bg: 'color-mix(in srgb, var(--red) 8%, transparent)', border: 'color-mix(in srgb, var(--red) 20%, transparent)',  text: 'var(--red)',   badge: 'badge-red'    },
  favorable:  { bg: 'color-mix(in srgb, var(--green) 8%, transparent)', border: 'color-mix(in srgb, var(--green) 20%, transparent)', text: 'var(--green)', badge: 'badge-green'  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(val, format) {
  if (val === null || val === undefined) return '—';
  if (format === 'currency') return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (format === 'integer') return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
  return Number(val).toFixed(2);
}

// Role-aware severity for DiscrepancyChecker (fix 7)
function getCheckerSeverity(pct, role) {
  const abs = Math.abs(pct || 0);
  if (abs <= 5) return { key: 'ok', label: 'OK — Less than 5%', note: 'Within 5% — verify internally' };
  const partnerHigher = (pct || 0) > 0;
  if (role === 'supply') {
    if (partnerHigher) return abs > 20 ? { key: 'critical', label: 'Critical — Loss' } : { key: 'warning', label: 'Warning — Loss' };
    return abs > 20 ? { key: 'critical', label: 'Critical — Over-count' } : { key: 'warning', label: 'Warning — Over-count' };
  } else {
    if (partnerHigher) return abs > 20 ? { key: 'favorable', label: 'Highly Favorable' } : { key: 'favorable', label: 'Favorable' };
    return abs > 20 ? { key: 'critical', label: 'Critical — Loss' } : { key: 'warning', label: 'Warning — Loss' };
  }
}

function fmtPct(pct) {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${Number(pct).toFixed(2)}%`;
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Manual Entry Table for DiscrepancyChecker ───────────────────────────────
function ManualCheckerTable({ rows, onChange }) {
  function addRow() { onChange([...rows, { date: '', impressions: '', revenue: '' }]); }
  function removeRow(i) { onChange(rows.filter((_, idx) => idx !== i)); }
  function updateRow(i, field, val) { const u = [...rows]; u[i] = { ...u[i], [field]: val }; onChange(u); }
  const inputStyle = { background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: '4px', padding: '5px 8px', fontSize: '11px', color: 'var(--text-primary)', width: '100%', fontFamily: 'var(--font-mono)' };
  return (
    <div>
      <div style={{ overflowX: 'auto', marginBottom: '8px' }}>
        <table className="data-table" style={{ minWidth: '400px' }}>
          <thead><tr><th>Date</th><th>Impressions</th><th>Revenue ($)</th><th style={{ width: 28 }}></th></tr></thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td><input type="date" style={inputStyle} value={row.date} onChange={e => updateRow(i, 'date', e.target.value)} /></td>
                <td><input type="number" style={inputStyle} placeholder="0" value={row.impressions} onChange={e => updateRow(i, 'impressions', e.target.value)} /></td>
                <td><input type="number" style={inputStyle} step="0.01" placeholder="0.00" value={row.revenue} onChange={e => updateRow(i, 'revenue', e.target.value)} /></td>
                <td><button onClick={() => removeRow(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', padding: '2px' }}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className="btn btn-secondary btn-sm" onClick={addRow}>+ Add Row</button>
      <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '8px' }}>{rows.filter(r => r.date).length} rows</span>
    </div>
  );
}

// ─── Input Panel ─────────────────────────────────────────────────────────────

function InputPanel({ label, color, value, onChange, onPreviewChange }) {
  const [mode, setMode] = useState('file'); // 'file' | 'url'
  const [fileName, setFileName] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // User-supplied overrides for date column header & date format. Default to
  // null = "use what backend detected". Bubbled up via onPreviewChange so the
  // parent passes them with /check.
  const [dateColumnOverride, setDateColumnOverride] = useState(null);
  const [dateFormatOverride, setDateFormatOverride] = useState(null);
  // Inventory (bundle/domain) overrides — same pattern as date overrides
  const [inventoryColumnOverride, setInventoryColumnOverride] = useState(null);
  const [inventoryShapeOverride,  setInventoryShapeOverride]  = useState(null);
  const fileRef = useRef(null);

  // Bubble up resolved detection + overrides to parent whenever they change
  useEffect(() => {
    if (!onPreviewChange) return;
    if (!preview) { onPreviewChange(null); return; }
    onPreviewChange({
      dateColumn:        dateColumnOverride || preview.dateColumn || null,
      dateFormat:        dateFormatOverride || preview.dateDetection?.format || null,
      detection:         preview.dateDetection || null,
      headers:           preview.headers || [],
      supportedFormats:  preview.supportedDateFormats || [],
      columnOverridden:  !!dateColumnOverride,
      formatOverridden:  !!dateFormatOverride,
      // Inventory bubble-up
      inventoryColumn:   inventoryColumnOverride || preview.inventoryColumn || null,
      inventoryShape:    inventoryShapeOverride  || preview.inventoryShape  || null,
      inventoryDetection: preview.inventoryDetection || null,
      inventoryColumnOverridden: !!inventoryColumnOverride,
      inventoryShapeOverridden:  !!inventoryShapeOverride,
    });
  }, [preview, dateColumnOverride, dateFormatOverride, inventoryColumnOverride, inventoryShapeOverride]); // eslint-disable-line

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const name = file.name;
    const ext = name.split('.').pop().toLowerCase();

    setFileName(name);
    setPreview(null);
    // Fresh file = fresh detection; clear any leftover overrides from the prior file
    setDateColumnOverride(null);
    setDateFormatOverride(null);
    setInventoryColumnOverride(null);
    setInventoryShapeOverride(null);

    try {
      let inputObj;
      if (ext === 'csv' || ext === 'txt' || ext === 'tsv') {
        const text = await readFileAsText(file);
        inputObj = { type: 'csv', data: text, name };
      } else if (ext === 'xlsx' || ext === 'xls') {
        const b64 = await readFileAsBase64(file);
        inputObj = { type: 'xlsx', data: b64, name };
      } else {
        toast.error('Unsupported file type. Use CSV, XLSX, or XLS.');
        return;
      }
      onChange(inputObj);
      toast.success(`Loaded: ${name}`);
      // Auto-preview
      setPreviewLoading(true);
      try {
        const { data } = await axios.post('/api/discrepancy/preview', { input: inputObj });
        setPreview(data);
      } catch {}
      setPreviewLoading(false);
    } catch (err) {
      toast.error(`Failed to read file: ${err.message}`);
    }
  }

  async function handleUrlChange(url) {
    onChange({ type: 'url', data: url, name: url });
  }

  async function previewUrl(url) {
    if (!url) return;
    setPreviewLoading(true);
    try {
      const { data } = await axios.post('/api/discrepancy/preview', { input: { type: 'url', data: url, name: url } });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Preview failed');
    } finally { setPreviewLoading(false); }
  }

  const borderColor = color === 'A' ? 'var(--accent)' : color === 'C' ? 'var(--green)' : 'var(--purple)';
  const onColor = color === 'A' ? 'var(--on-accent)' : color === 'C' ? 'var(--on-green)' : 'var(--on-purple)';
  const bgColor = color === 'A' ? 'color-mix(in srgb, var(--accent) 6%, transparent)' : color === 'C' ? 'color-mix(in srgb, var(--green) 6%, transparent)' : 'color-mix(in srgb, var(--purple) 6%, transparent)';

  return (
    <div style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 'var(--radius)', padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: borderColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: onColor, flexShrink: 0 }}>
          {color}
        </div>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>{label}</span>
      </div>

      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
        <button className={`btn btn-sm ${mode === 'file' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setMode('file'); setPreview(null); }}>
          <Upload size={12} /> Upload File
        </button>
        <button className={`btn btn-sm ${mode === 'url' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setMode('url'); setPreview(null); setFileName(null); }}>
          <Link size={12} /> API URL
        </button>
        <button className={`btn btn-sm ${mode === 'manual' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setMode('manual'); setPreview(null); setFileName(null); onChange({ type: 'manual', manualRows: [], name: 'Manual Entry' }); }}>
          <FileText size={12} /> Manual Entry
        </button>
      </div>

      {mode === 'manual' && (
        <ManualCheckerTable
          rows={value?.manualRows || []}
          onChange={rows => onChange({ type: 'manual', manualRows: rows, name: 'Manual Entry' })}
        />
      )}

      {mode === 'file' && (
        <div
          onClick={() => fileRef.current.click()}
          style={{ border: '2px dashed var(--border)', borderRadius: 'var(--radius-sm)', padding: '24px 16px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = borderColor}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
        >
          <Upload size={22} style={{ color: 'var(--text-muted)', marginBottom: '8px' }} />
          {fileName
            ? <div><div style={{ color: 'var(--green)', fontWeight: 600, fontSize: '12px' }}>{fileName}</div><div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Click to replace</div></div>
            : <div><div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Click to upload</div><div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>CSV, XLSX, XLS supported</div></div>}
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt,.tsv" style={{ display: 'none' }} onChange={handleFile} />
        </div>
      )}

      {mode === 'url' && (
        <div>
          <input
            className="input-field w-full"
            placeholder="https://api.example.com/report?date=2024-01-01"
            onChange={e => handleUrlChange(e.target.value)}
            onBlur={e => previewUrl(e.target.value)}
          />
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Must return JSON (array or object with rows) or CSV. Tab out to preview.
          </div>
        </div>
      )}

      {/* Preview */}
      {previewLoading && <div style={{ textAlign: 'center', padding: '10px' }}><span className="spinner" /></div>}
      {preview && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            Preview — {preview.rowCount} rows detected
            {preview.sourceFormat && (
              <span className={`badge ${preview.sourceFormat === 'xml' ? 'badge-amber' : preview.sourceFormat === 'json' ? 'badge-cyan' : 'badge-muted'}`} style={{ fontSize: '9px', textTransform: 'uppercase' }}>
                {preview.sourceFormat}
              </span>
            )}
            {preview.convertedFrom === 'xml' && (
              <span className="badge badge-amber" style={{ fontSize: '9px' }}>⟳ XML→JSON auto-converted</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>
            {Object.entries(preview.colMap || {}).map(([field, col]) => (
              <span key={field} className="badge badge-green" style={{ fontSize: '10px' }}>✓ {field}: "{col}"</span>
            ))}
          </div>
          {preview.headers?.filter(h => !Object.values(preview.colMap || {}).includes(h)).map(h => (
            <span key={h} className="badge badge-muted" style={{ fontSize: '9px', marginRight: '3px' }}>{h}</span>
          ))}

          {/* ── Date detection strip (override-able) ───────────────────── */}
          {preview.dateColumn && (() => {
            const effectiveCol    = dateColumnOverride || preview.dateColumn;
            const effectiveFormat = dateFormatOverride || preview.dateDetection?.format;
            const isAmbiguous     = !dateFormatOverride && preview.dateDetection?.ambiguous;
            const noFormat        = !effectiveFormat;
            // Tone the strip: red if no format, amber if ambiguous, cyan if confident
            const tone = noFormat   ? { bg:'color-mix(in srgb, var(--red) 10%, transparent)', bd:'color-mix(in srgb, var(--red) 35%, transparent)', tx:'var(--red)',  icon:'⚠' }
                       : isAmbiguous? { bg:'color-mix(in srgb, var(--amber) 10%, transparent)', bd:'color-mix(in srgb, var(--amber) 35%, transparent)', tx:'var(--amber)', icon:'⚠' }
                                    : { bg:'color-mix(in srgb, var(--cyan) 8%, transparent)', bd:'color-mix(in srgb, var(--cyan) 30%, transparent)',  tx:'var(--cyan)',  icon:'✓' };
            return (
              <div style={{ marginTop:'10px', padding:'8px 10px', background:tone.bg, border:`1px solid ${tone.bd}`, borderRadius:'var(--radius-sm)' }}>
                <div style={{ fontSize:'10px', color:tone.tx, fontWeight:700, marginBottom:'6px', display:'flex', alignItems:'center', gap:'5px' }}>
                  {tone.icon} Date detection
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                  <div>
                    <label style={{ fontSize:'9px', color:'var(--text-muted)', display:'block', marginBottom:'2px' }}>Date column</label>
                    <select className="input-field" style={{ fontSize:'11px', width:'100%', padding:'4px 6px' }}
                      value={effectiveCol}
                      onChange={e => setDateColumnOverride(e.target.value === preview.dateColumn ? null : e.target.value)}>
                      {preview.headers.map(h => <option key={h} value={h}>{h}{h === preview.dateColumn ? ' (auto)' : ''}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:'9px', color:'var(--text-muted)', display:'block', marginBottom:'2px' }}>Date format</label>
                    <select className="input-field" style={{ fontSize:'11px', width:'100%', padding:'4px 6px' }}
                      value={effectiveFormat || ''}
                      onChange={e => setDateFormatOverride(e.target.value === preview.dateDetection?.format ? null : (e.target.value || null))}>
                      {!effectiveFormat && <option value="">— pick a format —</option>}
                      {(preview.supportedDateFormats || []).map(f => (
                        <option key={f} value={f}>
                          {f}{f === preview.dateDetection?.format ? ' (auto)' : ''}
                          {preview.dateDetection?.candidates?.includes(f) && f !== preview.dateDetection?.format ? ' (also fits)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {isAmbiguous && preview.dateDetection?.reason && (
                  <div style={{ fontSize:'9px', color:'var(--text-muted)', marginTop:'5px', lineHeight:1.4 }}>
                    {preview.dateDetection.reason}. Click the dropdown to override.
                  </div>
                )}
                {noFormat && (
                  <div style={{ fontSize:'9px', color:'var(--text-muted)', marginTop:'5px', lineHeight:1.4 }}>
                    Couldn't detect a format from this column's values. Pick one manually to run the check.
                  </div>
                )}
              </div>
            );
          })()}

          {/* If header-match didn't find a date column at all */}
          {!preview.dateColumn && (
            <div style={{ marginTop:'10px', padding:'8px 10px', background:'color-mix(in srgb, var(--red) 10%, transparent)', border:'1px solid color-mix(in srgb, var(--red) 35%, transparent)', borderRadius:'var(--radius-sm)', fontSize:'10px', color:'var(--red)' }}>
              ⚠ No date column found. Available columns: {preview.headers.join(', ')}. Rename one to "Date" or "Day", or pick from the dropdown:
              <select className="input-field" style={{ fontSize:'11px', width:'100%', padding:'4px 6px', marginTop:'6px' }}
                value={dateColumnOverride || ''}
                onChange={e => setDateColumnOverride(e.target.value || null)}>
                <option value="">— pick a column —</option>
                {preview.headers.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          )}

          {/* ── Inventory detection strip (override-able) ─────────────── */}
          {/* Only shown when a likely inventory column was identified (header
              match or value-scan). Same tone language as the date strip:
              cyan = confident, amber = mixed-shape / ambiguous, red = no shape. */}
          {preview.inventoryColumn && (() => {
            const effectiveCol   = inventoryColumnOverride || preview.inventoryColumn;
            const effectiveShape = inventoryShapeOverride  || preview.inventoryShape;
            const isMixed        = !inventoryShapeOverride && preview.inventoryDetection?.shape === 'mixed';
            const isUnknown      = !inventoryShapeOverride && preview.inventoryDetection?.shape === 'unknown';
            const tone = isUnknown ? { bg:'color-mix(in srgb, var(--red) 10%, transparent)', bd:'color-mix(in srgb, var(--red) 35%, transparent)', tx:'var(--red)',   icon:'⚠' }
                       : isMixed   ? { bg:'color-mix(in srgb, var(--amber) 10%, transparent)', bd:'color-mix(in srgb, var(--amber) 35%, transparent)', tx:'var(--amber)', icon:'⚠' }
                                   : { bg:'color-mix(in srgb, var(--cyan) 8%, transparent)', bd:'color-mix(in srgb, var(--cyan) 30%, transparent)',  tx:'var(--cyan)',  icon:'✓' };
            const det = preview.inventoryDetection || {};
            return (
              <div style={{ marginTop:'10px', padding:'8px 10px', background:tone.bg, border:`1px solid ${tone.bd}`, borderRadius:'var(--radius-sm)' }}>
                <div style={{ fontSize:'10px', color:tone.tx, fontWeight:700, marginBottom:'6px', display:'flex', alignItems:'center', gap:'5px' }}>
                  {tone.icon} Inventory detection
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                  <div>
                    <label style={{ fontSize:'9px', color:'var(--text-muted)', display:'block', marginBottom:'2px' }}>Inventory column</label>
                    <select className="input-field" style={{ fontSize:'11px', width:'100%', padding:'4px 6px' }}
                      value={effectiveCol}
                      onChange={e => setInventoryColumnOverride(e.target.value === preview.inventoryColumn ? null : e.target.value)}>
                      {preview.headers.map(h => <option key={h} value={h}>{h}{h === preview.inventoryColumn ? ' (auto)' : ''}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:'9px', color:'var(--text-muted)', display:'block', marginBottom:'2px' }}>Type</label>
                    <select className="input-field" style={{ fontSize:'11px', width:'100%', padding:'4px 6px' }}
                      value={effectiveShape || ''}
                      onChange={e => setInventoryShapeOverride(e.target.value === preview.inventoryShape ? null : (e.target.value || null))}>
                      {!effectiveShape && <option value="">— pick a type —</option>}
                      <option value="bundle">App Bundles{preview.inventoryShape === 'bundle' ? ' (auto)' : ''}</option>
                      <option value="domain">Website Domains{preview.inventoryShape === 'domain' ? ' (auto)' : ''}</option>
                    </select>
                  </div>
                </div>
                {(isMixed || isUnknown) && (
                  <div style={{ fontSize:'9px', color:'var(--text-muted)', marginTop:'5px', lineHeight:1.4 }}>
                    {isMixed && `Column has ${det.bundleCount} bundle-shape and ${det.domainCount} domain-shape rows out of ${det.sampleSize} sampled. Pick one explicitly.`}
                    {isUnknown && `Couldn't tell if this column has app bundles or website domains. Pick one to run the check.`}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryMetricCard({ metric, data, partyAName, partyBName, adServerName, adServerData }) {
  const sev = SEVERITY_COLORS[data.severity] || SEVERITY_COLORS.ok;
  return (
    <div style={{ background: sev.bg, border: `1px solid ${sev.border}`, borderRadius: 'var(--radius)', padding: '14px 16px' }}>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>{metric.label}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', gap: '6px' }}>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{partyAName}</div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{fmt(data.a, metric.format)}</div>
        </div>
        {adServerName && adServerData && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--green)' }}>{adServerName}</div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{fmt(adServerData[metric.key]?.a ?? adServerData[metric.key]?.b, metric.format)}</div>
          </div>
        )}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{partyBName}</div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{fmt(data.b, metric.format)}</div>
        </div>
      </div>
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Diff: {fmt(data.diff, metric.format)}</span>
        <span style={{ fontSize: '13px', fontWeight: 700, color: sev.text }}>{fmtPct(data.pct)}</span>
      </div>
    </div>
  );
}


// ─── Results table ────────────────────────────────────────────────────────────

function ResultsTable({ rows, checkType, partyAName, partyBName, summary, availableMetrics }) {
  // Filter to only metrics that have real data in at least one party
  const visibleMetrics = availableMetrics
    ? METRICS.filter(m => availableMetrics.includes(m.key))
    : METRICS;
  const [filter, setFilter] = useState('all');
  const [sortKey, setSortKey] = useState(checkType === 'bundle' ? 'revenue' : 'date');
  const [sortDir, setSortDir] = useState(checkType === 'bundle' ? -1 : 1);

  // Reset to revenue-descending when switching to bundle mode
  React.useEffect(() => {
    if (checkType === 'bundle') { setSortKey('revenue'); setSortDir(-1); }
    else { setSortKey('date'); setSortDir(1); }
  }, [checkType]);
  const [page, setPage] = useState(0);
  const PER_PAGE = 50;

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => -d);
    else { setSortKey(key); setSortDir(1); }
    setPage(0);
  }

  const filtered = rows.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'critical') return r.severity === 'critical';
    if (filter === 'warning') return r.severity === 'warning';
    if (filter === 'ok') return r.severity === 'ok';
    if (filter === 'missing_a') return !r.inA;
    if (filter === 'missing_b') return !r.inB;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    // For revenue: sort by absolute diff value (dollar amount, not %)
    // For other metrics: sort by % discrepancy
    let av, bv;
    if (sortKey === 'revenue') {
      av = Math.abs(a.metrics?.revenue?.diff ?? 0);
      bv = Math.abs(b.metrics?.revenue?.diff ?? 0);
    } else if (a.metrics?.[sortKey] !== undefined) {
      av = Math.abs(a.metrics[sortKey]?.pct ?? 0);
      bv = Math.abs(b.metrics[sortKey]?.pct ?? 0);
    } else {
      av = a[sortKey] ?? 0;
      bv = b[sortKey] ?? 0;
    }
    if (typeof av === 'string') return av.localeCompare(bv) * sortDir;
    return (av - bv) * sortDir;
  });

  const paged = sorted.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const totalPages = Math.ceil(sorted.length / PER_PAGE);

  function exportCSV() {
    const csvMetrics = availableMetrics ? METRICS.filter(m => availableMetrics.includes(m.key)) : METRICS;
    const headers = checkType === 'bundle'
      ? ['Date', 'Bundle', ...csvMetrics.flatMap(m => [`${m.label} (A)`, `${m.label} (B)`, `${m.label} Diff`, `${m.label} %`]), 'Severity']
      : ['Date', ...csvMetrics.flatMap(m => [`${m.label} (A)`, `${m.label} (B)`, `${m.label} Diff`, `${m.label} %`]), 'Severity'];
    const dataRows = sorted.map(r => {  // use sorted order so CSV matches what user sees
      const base = checkType === 'bundle' ? [r.date, r.bundle] : [r.date];
      const metrics = csvMetrics.flatMap(m => [r.metrics[m.key]?.a ?? '', r.metrics[m.key]?.b ?? '', r.metrics[m.key]?.diff ?? '', r.metrics[m.key]?.pct ?? '']);
      return [...base, ...metrics, r.severity];
    });
    const csv = [headers, ...dataRows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = 'discrepancy-report.csv'; a.click();
  }

  const Th = ({ label, sortable, sk }) => (
    <th onClick={sortable ? () => toggleSort(sk || label.toLowerCase()) : undefined}
      style={{ cursor: sortable ? 'pointer' : 'default', userSelect: 'none' }}>
      {label} {sortable && sortKey === (sk || label.toLowerCase()) ? (sortDir === 1 ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="card-title" style={{ margin: 0 }}><BarChart2 size={14} /> Row-by-Row Results <span className="badge badge-muted">{rows.length}</span></div>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: `All (${rows.length})` },
            { id: 'critical', label: `🔴 Critical` },
            { id: 'warning', label: `🟡 Warning` },
            { id: 'ok', label: `✅ OK — Less than 5%` },
            { id: 'missing_a', label: `Missing in A` },
            { id: 'missing_b', label: `Missing in B` },
          ].map(f => (
            <button key={f.id} className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setFilter(f.id); setPage(0); }}>
              {f.label}
            </button>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={exportCSV}>
          <Download size={12} /> Export CSV
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <Th label="Date" sortable sk="date" />
              {checkType === 'bundle' && <Th label="Bundle" sortable sk="bundle" />}
              {visibleMetrics.map(m => (
                <React.Fragment key={m.key}>
                  <th>{partyAName} {m.label}</th>
                  <th>{partyBName} {m.label}</th>
                  <th>Diff %</th>
                </React.Fragment>
              ))}
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((r, i) => {
              const sev = SEVERITY_COLORS[r.severity] || SEVERITY_COLORS.ok;
              return (
                <tr key={i} style={{ background: r.severity !== 'ok' ? sev.bg : undefined }}>
                  <td style={{ fontWeight: 600, color: 'var(--cyan)', fontSize: '13px' }}>{r.date}</td>
                  {checkType === 'bundle' && (
                    <td><code style={{ fontSize: '11px', color: 'var(--cyan)' }}>{r.bundle}</code></td>
                  )}
                  {visibleMetrics.map(m => {
                    const md = r.metrics[m.key] || {};
                    const msev = SEVERITY_COLORS[md.severity] || SEVERITY_COLORS.ok;
                    return (
                      <React.Fragment key={m.key}>
                        <td className="mono" style={{ fontSize: '11px' }}>{fmt(md.a, m.format)}</td>
                        <td className="mono" style={{ fontSize: '11px' }}>{fmt(md.b, m.format)}</td>
                        <td>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: msev.text }}>
                            {md.pct !== undefined ? fmtPct(md.pct) : '—'}
                          </span>
                          {md.diff !== undefined && md.diff !== 0 && (
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                              {fmt(md.diff, m.format)}
                            </div>
                          )}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td>
                    {!r.inA && <span className="badge badge-amber">Missing in A</span>}
                    {!r.inB && <span className="badge badge-amber">Missing in B</span>}
                    {r.inA && r.inB && <span className={`badge ${sev.badge}`}>{r.severity === 'ok' ? '✅ OK — Less than 5%' : r.severity === 'critical' ? '🔴 Critical' : '🟡 Warning'}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '12px', alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</button>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Page {page + 1} of {totalPages}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next ›</button>
        </div>
      )}
    </div>
  );
}

// ─── Three-Way Results Panel ──────────────────────────────────────────────────
function ThreeWayResultsPanel({ supplyResult, demandResult, supplyName, adServerName, demandName, checkType }) {
  const SEV_ORDER = { critical: 0, warning: 1, favorable: 2, ok: 3 };
  const worstSev = (r) => {
    if (!r) return 'ok';
    const sevs = Object.values(r.summary || {}).map(s => s?.severity).filter(Boolean);
    return sevs.sort((a,b) => (SEV_ORDER[a]??3) - (SEV_ORDER[b]??3))[0] || 'ok';
  };
  const supplyWorst = worstSev(supplyResult);
  const demandWorst = worstSev(demandResult);
  const overallWorst = [supplyWorst, demandWorst].sort((a,b) => (SEV_ORDER[a]??3) - (SEV_ORDER[b]??3))[0];
  const overallSev = SEVERITY_COLORS[overallWorst] || SEVERITY_COLORS.ok;

  const combined = {
    critical: (supplyResult?.stats?.critical||0) + (demandResult?.stats?.critical||0),
    warning:  (supplyResult?.stats?.warning||0)  + (demandResult?.stats?.warning||0),
    ok:       (supplyResult?.stats?.ok||0)        + (demandResult?.stats?.ok||0),
  };

  return (
    <div>
      {/* ── Inventory mismatch banner — fires per-side ──────────────────
          In 3-way mode there are two joins (supply→adserver, adserver→demand).
          Either or both can hit an inventory mismatch. Banner labels which. */}
      {supplyResult?.inventoryMismatch && (
        <div style={{
          marginBottom: '12px', padding: '14px 16px',
          background: 'color-mix(in srgb, var(--red) 10%, transparent)', border: '1.5px solid color-mix(in srgb, var(--red) 45%, transparent)',
          borderRadius: 'var(--radius-md)', display: 'flex', gap: '12px', alignItems: 'flex-start'
        }}>
          <AlertTriangle size={20} style={{ color:'var(--red)', flexShrink:0, marginTop:'1px' }} />
          <div>
            <div style={{ fontWeight:800, fontSize:'14px', color:'var(--red)', marginBottom:'4px' }}>
              Inventory mismatch on {supplyName} → {adServerName}
            </div>
            <div style={{ fontSize:'13px', color:'var(--text-secondary)', lineHeight:1.55 }}>
              {supplyResult.inventoryMismatch.message}
            </div>
          </div>
        </div>
      )}
      {demandResult?.inventoryMismatch && (
        <div style={{
          marginBottom: '12px', padding: '14px 16px',
          background: 'color-mix(in srgb, var(--red) 10%, transparent)', border: '1.5px solid color-mix(in srgb, var(--red) 45%, transparent)',
          borderRadius: 'var(--radius-md)', display: 'flex', gap: '12px', alignItems: 'flex-start'
        }}>
          <AlertTriangle size={20} style={{ color:'var(--red)', flexShrink:0, marginTop:'1px' }} />
          <div>
            <div style={{ fontWeight:800, fontSize:'14px', color:'var(--red)', marginBottom:'4px' }}>
              Inventory mismatch on {adServerName} → {demandName}
            </div>
            <div style={{ fontSize:'13px', color:'var(--text-secondary)', lineHeight:1.55 }}>
              {demandResult.inventoryMismatch.message}
            </div>
          </div>
        </div>
      )}

      {/* Chain header + combined stats */}
      <div style={{ padding:'14px 16px', background:'var(--bg-elevated)', border:`1px solid ${overallSev.border}`, borderRadius:'var(--radius)', marginBottom:'16px' }}>
        {/* Chain label */}
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'14px', flexWrap:'wrap' }}>
          <Layers size={14} style={{ color:'var(--cyan)' }}/>
          <span style={{ fontWeight:700, fontSize:'13px', color:'var(--text-primary)' }}>3-Way Check</span>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px' }}>
            <span style={{ color:'var(--cyan)', fontWeight:700 }}>{supplyName}</span>
            <ArrowRight size={12} style={{ color:'var(--text-muted)'}}/>
            <span style={{ color:'var(--green)', fontWeight:700 }}>{adServerName}</span>
            <ArrowRight size={12} style={{ color:'var(--text-muted)'}}/>
            <span style={{ color:'var(--purple)', fontWeight:700 }}>{demandName}</span>
          </div>
          <span className={`badge ${overallSev.badge}`} style={{ marginLeft:'auto' }}>{overallWorst.toUpperCase()}</span>
        </div>

        {/* Combined stat row */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px' }}>
          {[
            { label:'Critical', v:combined.critical, color:'var(--red)' },
            { label:'Warnings', v:combined.warning,  color:'var(--amber)' },
            { label:'OK',       v:combined.ok,        color:'var(--cyan)' },
          ].map(({label,v,color}) => (
            <div key={label} style={{ textAlign:'center', padding:'8px', background:'var(--bg-base)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
              <div style={{ fontSize:'20px', fontWeight:900, color, fontFamily:'var(--font-mono)' }}>{v}</div>
              <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', marginTop:'2px' }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Two result panels side by side */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
        {/* Supply vs Ad Server */}
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'10px' }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--cyan)' }}/>
            <span style={{ fontSize:'11px', fontWeight:800, color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{supplyName}</span>
            <ArrowRight size={11} style={{ color:'var(--text-muted)'}}/>
            <span style={{ fontSize:'11px', fontWeight:800, color:'var(--green)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{adServerName}</span>
          </div>
          {supplyResult ? (
            <>
              <div className="grid-4" style={{ marginBottom:'10px', gridTemplateColumns:'repeat(3,1fr)', display:'grid', gap:'8px' }}>
                {[
                  { label:'Critical', v:supplyResult.stats.critical, color:'var(--red)' },
                  { label:'Warning',  v:supplyResult.stats.warning,  color:'var(--amber)' },
                  { label:'OK',       v:supplyResult.stats.ok,        color:'var(--cyan)' },
                ].map(({label,v,color}) => (
                  <div key={label} style={{ textAlign:'center', padding:'6px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:'16px', fontWeight:900, color, fontFamily:'var(--font-mono)' }}>{v}</div>
                    <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase' }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:'10px' }}>
                <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'8px' }}>
                  Overall — {supplyName} vs {adServerName}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:`repeat(${(supplyResult.availableMetrics||['impressions','revenue']).length},1fr)`, gap:'8px' }}>
                  {METRICS.filter(m => !supplyResult.availableMetrics || supplyResult.availableMetrics.includes(m.key)).map(m => (
                    <SummaryMetricCard key={m.key} metric={m} data={supplyResult.summary[m.key]} partyAName={supplyName} partyBName={adServerName} />
                  ))}
                </div>
              </div>
              <ResultsTable rows={supplyResult.rows} checkType={checkType} partyAName={supplyName} partyBName={adServerName} summary={supplyResult.summary} availableMetrics={supplyResult.availableMetrics} compact />
            </>
          ) : (
            <div className="alert alert-warning"><AlertTriangle size={12}/> No result — upload data for {supplyName} and {adServerName}</div>
          )}
        </div>

        {/* Ad Server vs Demand */}
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'10px' }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--green)' }}/>
            <span style={{ fontSize:'11px', fontWeight:800, color:'var(--green)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{adServerName}</span>
            <ArrowRight size={11} style={{ color:'var(--text-muted)'}}/>
            <span style={{ fontSize:'11px', fontWeight:800, color:'var(--purple)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{demandName}</span>
          </div>
          {demandResult ? (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'10px' }}>
                {[
                  { label:'Critical', v:demandResult.stats.critical, color:'var(--red)' },
                  { label:'Warning',  v:demandResult.stats.warning,  color:'var(--amber)' },
                  { label:'OK',       v:demandResult.stats.ok,        color:'var(--cyan)' },
                ].map(({label,v,color}) => (
                  <div key={label} style={{ textAlign:'center', padding:'6px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:'16px', fontWeight:900, color, fontFamily:'var(--font-mono)' }}>{v}</div>
                    <div style={{ fontSize:'9px', color:'var(--text-muted)', textTransform:'uppercase' }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:'10px' }}>
                <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'8px' }}>
                  Overall — {adServerName} vs {demandName}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:`repeat(${(demandResult.availableMetrics||['impressions','revenue']).length},1fr)`, gap:'8px' }}>
                  {METRICS.filter(m => !demandResult.availableMetrics || demandResult.availableMetrics.includes(m.key)).map(m => (
                    <SummaryMetricCard key={m.key} metric={m} data={demandResult.summary[m.key]} partyAName={adServerName} partyBName={demandName} />
                  ))}
                </div>
              </div>
              <ResultsTable rows={demandResult.rows} checkType={checkType} partyAName={adServerName} partyBName={demandName} summary={demandResult.summary} availableMetrics={demandResult.availableMetrics} compact />
            </>
          ) : (
            <div className="alert alert-warning"><AlertTriangle size={12}/> No result — upload data for {adServerName} and {demandName}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const RECOMMENDATIONS = {
  numbers: [
    'Reports from both parties must cover the same date range and timezone',
    'Both reports must include: Date, Impressions, and Revenue — Wins and eCPM are optional context columns',
    'Ensure revenue figures use the same currency (USD recommended)',
    'Discrepancy status is based on Impressions and Revenue only — Wins and eCPM are shown for reference but do not affect severity',
    'Discrepancy > 20% is flagged as Critical — investigate billing terms and filtering differences',
    'Discrepancy 5–20% is flagged as Warning — common causes: timezone mismatch, impression counting methodology',
    'Discrepancy < 5% is generally acceptable in programmatic advertising',
  ],
  bundle: [
    'Reports from both parties must cover the same date range and timezone',
    'Both reports must include: Date, Bundle ID, Impressions, and Revenue — Wins and eCPM are optional',
    'Bundle IDs must match exactly — check for trailing spaces or case differences',
    'Ensure both parties use the same bundle ID format (e.g. com.example.app vs 1234567890)',
    'Missing bundles in one report will be flagged as "Missing in A" or "Missing in B"',
    'Discrepancy status is based on Impressions and Revenue — high discrepancy on specific bundles may indicate filtering or blocklist differences',
  ],
};

export default function DiscrepancyChecker() {
  const [checkType, setCheckType] = useState('numbers');
  const [threeWay, setThreeWay] = useState(false);   // 3-way toggle

  // Party A (Supply)
  const [partyA, setPartyA] = useState(null);
  const [partyAName, setPartyAName] = useState('Party A');
  const [partyARole, setPartyARole] = useState('supply');

  // Ad Server (middle — only in 3-way mode)
  const [partyAS, setPartyAS] = useState(null);
  const [partyASName, setPartyASName] = useState('Ad Server');

  // Party B (Demand)
  const [partyB, setPartyB] = useState(null);
  const [partyBName, setPartyBName] = useState('Party B');
  const partyBRole = partyARole === 'supply' ? 'demand' : 'supply';

  // Per-file preview/override payloads from InputPanel (date column + format).
  // Stays null until the panel renders its preview; carries the user's overrides
  // so /check receives them in the request body.
  const [previewA,  setPreviewA]  = useState(null);
  const [previewAS, setPreviewAS] = useState(null);
  const [previewB,  setPreviewB]  = useState(null);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // 3-way results
  const [supplyResult, setSupplyResult] = useState(null);
  const [demandResult, setDemandResult] = useState(null);

  const [showRecs, setShowRecs] = useState(true);

  // Reset results when switching modes
  function toggleThreeWay() {
    setThreeWay(v => !v);
    setResult(null);
    setSupplyResult(null);
    setDemandResult(null);
  }

  async function runCheck() {
    // Helper: produce dateColumn/dateFormat override fields for a given preview
    // payload. Only sends an override if the user actually picked one; otherwise
    // backend's own detection stays authoritative.
    function overrideFields(prefix, p) {
      return {
        [`dateColumnOverride${prefix}`]: p?.columnOverridden ? p.dateColumn : null,
        [`dateFormatOverride${prefix}`]: p?.formatOverridden ? p.dateFormat : null,
        // Inventory overrides — only sent for bundle/website check, but harmless to
        // send always since backend ignores them on numbers check.
        [`inventoryColumnOverride${prefix}`]: p?.inventoryColumnOverridden ? p.inventoryColumn : null,
        [`inventoryShapeOverride${prefix}`]:  p?.inventoryShapeOverridden  ? p.inventoryShape  : null,
      };
    }
    // Helper: refuse to run if we have no usable date format for a file. Backend
    // will reject it anyway, but failing client-side gives the user a clearer
    // error tied to the file rather than a generic 400.
    function requireFormat(name, p) {
      if (p && !p.dateFormat) { toast.error(`Pick a date format for ${name} before running the check`); return false; }
      return true;
    }

    if (threeWay) {
      if (!partyA)  return toast.error(`Upload data for ${partyAName}`);
      if (!partyAS) return toast.error(`Upload data for ${partyASName}`);
      if (!partyB)  return toast.error(`Upload data for ${partyBName}`);
      if (!requireFormat(partyAName, previewA))   return;
      if (!requireFormat(partyASName, previewAS)) return;
      if (!requireFormat(partyBName, previewB))   return;

      setLoading(true);
      setSupplyResult(null);
      setDemandResult(null);

      try {
        // Run both checks in parallel. Supply check: partyA → partyAS. Demand
        // check: partyAS → partyB. Each side gets its own date overrides.
        const [resSupply, resDemand] = await Promise.all([
          axios.post('/api/discrepancy/check', {
            partyA: { ...partyA,  name: partyAName,  role: 'supply'    },
            partyB: { ...partyAS, name: partyASName, role: 'adserver'  },
            checkType,
            ...overrideFields('A', previewA),
            ...overrideFields('B', previewAS),
          }, { timeout: 27000 }),
          axios.post('/api/discrepancy/check', {
            partyA: { ...partyAS, name: partyASName, role: 'adserver' },
            partyB: { ...partyB,  name: partyBName,  role: 'demand'   },
            checkType,
            ...overrideFields('A', previewAS),
            ...overrideFields('B', previewB),
          }, { timeout: 27000 }),
        ]);

        setSupplyResult(resSupply.data);
        setDemandResult(resDemand.data);

        const totalCritical = (resSupply.data.stats?.critical || 0) + (resDemand.data.stats?.critical || 0);
        const totalWarning  = (resSupply.data.stats?.warning  || 0) + (resDemand.data.stats?.warning  || 0);
        if (totalCritical > 0) toast.error(`${totalCritical} critical discrepancies across both comparisons`);
        else if (totalWarning > 0) toast(`${totalWarning} warnings across both comparisons`, { icon: '⚠️' });
        else toast.success('3-way check complete — all within acceptable range');
      } catch (e) {
        const errMsg = e.response?.data?.error || e.message || 'Check failed';
        if (e.code === 'ECONNABORTED' || errMsg.includes('timeout')) {
          toast.error('Check timed out — try filtering to a shorter date range.', { duration: 8000 });
        } else {
          toast.error(errMsg);
        }
      } finally { setLoading(false); }
      return;
    }

    // ── Standard 2-way check ──────────────────────────────────────────────────
    if (!partyA) return toast.error('Upload or enter a URL for Party A');
    if (!partyB) return toast.error('Upload or enter a URL for Party B');
    if (!requireFormat(partyAName, previewA)) return;
    if (!requireFormat(partyBName, previewB)) return;

    const WARN_ROWS = 100000;
    if ((partyA.estimatedRows || 0) > WARN_ROWS || (partyB.estimatedRows || 0) > WARN_ROWS) {
      const largeParty = (partyA.estimatedRows || 0) > WARN_ROWS ? partyAName : partyBName;
      const count = Math.max(partyA.estimatedRows || 0, partyB.estimatedRows || 0);
      toast(`⚠️ ${largeParty} has ~${count.toLocaleString()} rows. Only the first 100,000 will be processed.`, { duration: 8000, icon: '⚠️' });
    }

    setLoading(true); setResult(null);

    try {
      const { data } = await axios.post('/api/discrepancy/check', {
        partyA: { ...partyA, name: partyAName, role: partyARole },
        partyB: { ...partyB, name: partyBName, role: partyBRole },
        checkType,
        ...overrideFields('A', previewA),
        ...overrideFields('B', previewB),
      }, { timeout: 27000 });
      setResult(data);

      if (data.truncationWarnings?.length > 0) {
        data.truncationWarnings.forEach(w => toast(w, { icon: '⚠️', duration: 8000 }));
      }

      if (data.stats.critical > 0) toast.error(`${data.stats.critical} critical discrepancies found`);
      else if (data.stats.warning > 0) toast(`${data.stats.warning} warnings found`, { icon: '⚠️' });
      else toast.success('Check complete — all within acceptable range');
    } catch (e) {
      const errMsg = e.response?.data?.error || e.message || 'Check failed';
      if (e.code === 'ECONNABORTED' || errMsg.includes('timeout') || errMsg.includes('timed out')) {
        toast.error('Check timed out — your files are very large. Try filtering to a shorter date range.', { duration: 8000 });
      } else {
        toast.error(errMsg);
      }
    } finally { setLoading(false); }
  }

  return (
    <div>
      <h1 className="page-title">Discrepancy Checker</h1>
      <p className="page-subtitle">Compare reporting numbers between two parties — or run a full Supply → Ad Server → Demand 3-way chain check</p>

      {/* Check type selector + 3-way toggle */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'10px', marginBottom:'12px' }}>
          <div className="card-title" style={{ margin:0 }}><GitCompare size={14} /> Check Type</div>
          {/* 3-way toggle */}
          <button
            onClick={toggleThreeWay}
            style={{
              display:'flex', alignItems:'center', gap:'8px',
              padding:'7px 14px', borderRadius:'var(--radius-sm)', fontWeight:700, fontSize:'12px', cursor:'pointer',
              background: threeWay ? 'color-mix(in srgb, var(--green) 10%, transparent)' : 'var(--bg-elevated)',
              border: `1px solid ${threeWay ? 'color-mix(in srgb, var(--green) 50%, transparent)' : 'var(--border)'}`,
              color: threeWay ? 'var(--green)' : 'var(--text-muted)',
              transition: 'all 0.15s',
            }}
          >
            <Server size={13}/>
            Include Ad Server
            <span style={{
              display:'inline-flex', alignItems:'center', justifyContent:'center',
              width:32, height:16, borderRadius:8, background: threeWay ? 'var(--green)' : 'var(--track-off)',
              transition:'background 0.2s', position:'relative',
            }}>
              <span style={{
                position:'absolute', width:12, height:12, borderRadius:'50%',
                background: threeWay ? 'var(--on-green)' : 'var(--knob)',
                boxShadow:'0 1px 3px rgba(0,0,0,0.3)',
                left: threeWay ? 18 : 2, transition:'left 0.2s',
              }}/>
            </span>
            {threeWay
              ? <span style={{ fontSize:'9px', background:'color-mix(in srgb, var(--green) 15%, transparent)', border:'1px solid color-mix(in srgb, var(--green) 40%, transparent)', borderRadius:'3px', padding:'1px 5px', color:'var(--green)', fontWeight:800 }}>ON</span>
              : <span style={{ fontSize:'9px', background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:'3px', padding:'1px 5px', color:'var(--text-muted)', fontWeight:800 }}>OFF</span>
            }
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {[
            { id: 'numbers', label: 'Numbers Check', desc: 'Compare Impressions and Revenue aggregated by date — Wins and eCPM shown as context', icon: BarChart2 },
            { id: 'bundle',  label: 'Bundle / Website Check', desc: 'Compare metrics broken down by App Bundle ID OR Website Domain and date — auto-detects which dimension your reports have', icon: FileText },
          ].map(({ id, label, desc, icon: Icon }) => (
            <div key={id} onClick={() => { setCheckType(id); setResult(null); setSupplyResult(null); setDemandResult(null); }} style={{
              padding: '14px 16px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              background: checkType === id ? 'var(--cyan-dim)' : 'var(--bg-elevated)',
              border: `2px solid ${checkType === id ? 'var(--cyan)' : 'var(--border)'}`,
              transition: 'all 0.15s'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Icon size={15} style={{ color: checkType === id ? 'var(--cyan)' : 'var(--text-muted)' }} />
                <span style={{ fontWeight: 600, color: checkType === id ? 'var(--cyan)' : 'var(--text-primary)', fontSize: '13px' }}>{label}</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{desc}</div>
            </div>
          ))}
        </div>

        {/* 3-way info banner */}
        {threeWay && (
          <div style={{ marginTop:'10px', padding:'10px 12px', background:'color-mix(in srgb, var(--green) 5%, transparent)', border:'1px solid color-mix(in srgb, var(--green) 20%, transparent)', borderRadius:'var(--radius-sm)', fontSize:'12px', color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:'8px' }}>
            <Server size={13} style={{ color:'var(--green)', flexShrink:0 }}/>
            <span>3-way mode: upload data for all three parties. Two checks will run — <strong style={{color:'var(--cyan)'}}>Supply vs Ad Server</strong> and <strong style={{color:'var(--purple)'}}>Ad Server vs Demand</strong> — and results are shown side by side.</span>
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="card" style={{ marginBottom: '16px', borderColor: 'color-mix(in srgb, var(--cyan) 20%, transparent)' }}>
        <div onClick={() => setShowRecs(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          {showRecs ? <ChevronDown size={13} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={13} style={{ color: 'var(--text-muted)' }} />}
          <div className="card-title" style={{ margin: 0 }}><Info size={13} style={{ color: 'var(--cyan)' }} /> Recommendations & Requirements</div>
        </div>
        {showRecs && (
          <div style={{ marginTop: '12px' }}>
            {RECOMMENDATIONS[checkType].map((rec, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', padding: '5px 0', fontSize: '12px', color: 'var(--text-secondary)', borderBottom: i < RECOMMENDATIONS[checkType].length - 1 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ color: 'var(--cyan)', flexShrink: 0 }}>✦</span>
                {rec}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── 3-way mode: Party name inputs ──────────────────────────────────── */}
      {threeWay && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr auto 1fr', gap:'8px', alignItems:'center', marginBottom:'12px' }}>
          {/* Supply */}
          <div style={{ background:'var(--bg-card)', border:'1px solid color-mix(in srgb, var(--cyan) 25%, transparent)', borderRadius:'var(--radius)', padding:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'8px' }}>
              <div style={{ width:22, height:22, borderRadius:'50%', background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:700, color:'var(--on-accent)' }}>A</div>
              <span style={{ fontSize:'11px', fontWeight:700, color:'var(--cyan)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Supply</span>
            </div>
            <input className="input-field w-full" value={partyAName} onChange={e => setPartyAName(e.target.value)} placeholder="e.g. PubMatic, Magnite" />
          </div>
          <ArrowRight size={16} style={{ color:'var(--text-muted)', flexShrink:0 }}/>
          {/* Ad Server */}
          <div style={{ background:'var(--bg-card)', border:'1px solid color-mix(in srgb, var(--green) 25%, transparent)', borderRadius:'var(--radius)', padding:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'8px' }}>
              <div style={{ width:22, height:22, borderRadius:'50%', background:'var(--green)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:700, color:'var(--on-green)' }}><Server size={11}/></div>
              <span style={{ fontSize:'11px', fontWeight:700, color:'var(--green)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Ad Server</span>
            </div>
            <input className="input-field w-full" value={partyASName} onChange={e => setPartyASName(e.target.value)} placeholder="e.g. Google Ad Manager, DFP" />
          </div>
          <ArrowRight size={16} style={{ color:'var(--text-muted)', flexShrink:0 }}/>
          {/* Demand */}
          <div style={{ background:'var(--bg-card)', border:'1px solid color-mix(in srgb, var(--purple) 25%, transparent)', borderRadius:'var(--radius)', padding:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'8px' }}>
              <div style={{ width:22, height:22, borderRadius:'50%', background:'var(--purple)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:700, color:'var(--on-purple)' }}>B</div>
              <span style={{ fontSize:'11px', fontWeight:700, color:'var(--purple)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Demand</span>
            </div>
            <input className="input-field w-full" value={partyBName} onChange={e => setPartyBName(e.target.value)} placeholder="e.g. DV360, TTD, Xandr" />
          </div>
        </div>
      )}

      {/* ── 2-way mode: Party role + name inputs ───────────────────────────── */}
      {!threeWay && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
          {/* Party A */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: 'var(--on-accent)', flexShrink: 0 }}>A</div>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>Party A</span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Role</label>
              <select className="select-field w-full" value={partyARole} onChange={e => setPartyARole(e.target.value)}>
                <option value="supply">Supply (SSP / Publisher)</option>
                <option value="demand">Demand (DSP / Advertiser)</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Name</label>
              <input className="input-field w-full" value={partyAName} onChange={e => setPartyAName(e.target.value)} placeholder={partyARole === 'supply' ? 'e.g. PubMatic, SSP, Publisher' : 'e.g. DV360, TTD, Advertiser'} />
            </div>
          </div>

          {/* Party B — role locked to opposite */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--purple)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: 'var(--on-purple)', flexShrink: 0 }}>B</div>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>Party B</span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Role</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '9px 12px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {partyBRole === 'demand' ? 'Demand (DSP / Advertiser)' : 'Supply (SSP / Publisher)'}
                </span>
                <span className="badge badge-muted" style={{ marginLeft: 'auto', fontSize: '10px' }}>auto</span>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px' }}>Always opposite to Party A</div>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Name</label>
              <input className="input-field w-full" value={partyBName} onChange={e => setPartyBName(e.target.value)} placeholder={partyBRole === 'demand' ? 'e.g. DV360, TTD, Advertiser' : 'e.g. PubMatic, SSP, Publisher'} />
            </div>
          </div>
        </div>
      )}

      {/* Input panels */}
      {threeWay ? (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'16px' }}>
          <InputPanel label={partyAName}  color="A" value={partyA}  onChange={setPartyA}  onPreviewChange={setPreviewA} />
          <InputPanel label={partyASName} color="C" value={partyAS} onChange={setPartyAS} onPreviewChange={setPreviewAS} />
          <InputPanel label={partyBName}  color="B" value={partyB}  onChange={setPartyB}  onPreviewChange={setPreviewB} />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <InputPanel label={partyAName} color="A" value={partyA} onChange={setPartyA} onPreviewChange={setPreviewA} />
          <InputPanel label={partyBName} color="B" value={partyB} onChange={setPartyB} onPreviewChange={setPreviewB} />
        </div>
      )}

      {/* Run button */}
      <div style={{ marginBottom: '24px' }}>
        <button className="btn btn-primary" onClick={runCheck} disabled={loading} style={{ minWidth: '180px' }}>
          {loading
            ? <><span className="spinner" /> Running Check{threeWay ? 's' : ''}...</>
            : <><GitCompare size={14} /> {threeWay ? 'Run 3-Way Check' : 'Run Discrepancy Check'}</>}
        </button>
      </div>

      {/* ── 3-way results ──────────────────────────────────────────────────── */}
      {threeWay && (supplyResult || demandResult) && (
        <ThreeWayResultsPanel
          supplyResult={supplyResult}
          demandResult={demandResult}
          supplyName={partyAName}
          adServerName={partyASName}
          demandName={partyBName}
          checkType={checkType}
        />
      )}

      {/* ── Standard 2-way results ─────────────────────────────────────────── */}
      {!threeWay && result && (
        <>
          {/* ── Inventory mismatch banner (1a+ safety net) ─────────────────
              When the two files describe different inventory dimensions (one
              has bundles, the other has domains), the join produces 0 rows
              and the summary cards would say "0 critical / 0 warning / 0 ok"
              — which looks like success. This banner makes that impossible
              to miss. */}
          {result.inventoryMismatch && (
            <div style={{
              marginBottom: '14px',
              padding: '14px 16px',
              background: 'color-mix(in srgb, var(--red) 10%, transparent)',
              border: '1.5px solid color-mix(in srgb, var(--red) 45%, transparent)',
              borderRadius: 'var(--radius-md)',
              display: 'flex', gap: '12px', alignItems: 'flex-start'
            }}>
              <AlertTriangle size={20} style={{ color: 'var(--red)', flexShrink: 0, marginTop: '1px' }} />
              <div>
                <div style={{ fontWeight: 800, fontSize: '14px', color: 'var(--red)', marginBottom: '4px' }}>
                  Inventory mismatch — results are not meaningful
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                  {result.inventoryMismatch.message}
                </div>
              </div>
            </div>
          )}

          {/* Format conversion notices */}
          {(result.partyAConverted || result.partyBConverted) && (
            <div style={{ marginBottom: '12px' }}>
              {result.partyAConverted === 'xml' && (
                <div className="alert alert-info" style={{ marginBottom: '6px' }}>
                  <Info size={12} />
                  <strong>{result.partyAName}</strong> returned XML — automatically converted to JSON for comparison
                  <span className="badge badge-amber" style={{ marginLeft: '8px' }}>XML → JSON</span>
                </div>
              )}
              {result.partyBConverted === 'xml' && (
                <div className="alert alert-info" style={{ marginBottom: '6px' }}>
                  <Info size={12} />
                  <strong>{result.partyBName}</strong> returned XML — automatically converted to JSON for comparison
                  <span className="badge badge-amber" style={{ marginLeft: '8px' }}>XML → JSON</span>
                </div>
              )}
            </div>
          )}

          {result.truncated && result.truncationWarnings?.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              {result.truncationWarnings.map((w, i) => (
                <div key={i} className="alert alert-warning" style={{ marginBottom: '6px' }}>
                  <AlertTriangle size={12} />
                  <div><strong>Large file — partial results:</strong> {w}</div>
                </div>
              ))}
            </div>
          )}

          {result.warnings && result.warnings.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {result.warnings.map((w, i) => (
                <div key={i} className="alert alert-info" style={{ marginBottom: '6px' }}>
                  <Info size={12} /> {w}
                </div>
              ))}
            </div>
          )}

          <div className="grid-4" style={{ marginBottom: '16px' }}>
            <div className="stat-card cyan">
              <div className="stat-label">Total Rows</div>
              <div className="stat-value">{result.stats.totalKeys}</div>
              <div className="stat-sub">{result.stats.inBoth} in both
                {result.truncated && <span style={{ color:'var(--amber)', fontSize:'10px', display:'block' }}>⚠ Partial — large file</span>}
              </div>
            </div>
            <div className="stat-card red">
              <div className="stat-label">Critical</div>
              <div className="stat-value red">{result.stats.critical}</div>
              <div className="stat-sub">Discrepancy &gt; 20%</div>
            </div>
            <div className="stat-card amber">
              <div className="stat-label">Warnings</div>
              <div className="stat-value amber">{result.stats.warning}</div>
              <div className="stat-sub">5–20% discrepancy</div>
            </div>
            <div className="stat-card green">
              <div className="stat-label">OK &lt; 5%</div>
              <div className="stat-value green">{result.stats.ok}</div>
              <div className="stat-sub">Within 5%</div>
            </div>
          </div>

          {(result.stats.onlyInA > 0 || result.stats.onlyInB > 0) && (
            <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
              <AlertTriangle size={13} />
              <div>
                {result.stats.onlyInA > 0 && <div>{result.stats.onlyInA} date{result.stats.onlyInA > 1 ? 's' : ''} only in <strong>{result.partyAName}</strong> — missing from {result.partyBName}</div>}
                {result.stats.onlyInB > 0 && <div>{result.stats.onlyInB} date{result.stats.onlyInB > 1 ? 's' : ''} only in <strong>{result.partyBName}</strong> — missing from {result.partyAName}</div>}
              </div>
            </div>
          )}


          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
              Overall Totals — {result.partyAName} vs {result.partyBName}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${(result.availableMetrics || METRICS.map(m=>m.key)).length}, 1fr)`, gap: '12px' }}>
              {METRICS.filter(m => !result.availableMetrics || result.availableMetrics.includes(m.key)).map(m => (
                <SummaryMetricCard key={m.key} metric={m} data={result.summary[m.key]} partyAName={result.partyAName} partyBName={result.partyBName} />
              ))}
            </div>
          </div>

          <ResultsTable
            rows={result.rows}
            checkType={result.checkType}
            partyAName={result.partyAName}
            partyBName={result.partyBName}
            summary={result.summary}
            availableMetrics={result.availableMetrics}
          />
        </>
      )}
    </div>
  );
}
